import { usePageableTable } from "../../../context/PageableTableContext";
import { memo, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { PageableTable } from "../../../components/table";
import { Link, useNavigate } from "react-router-dom";
import { DateTimeFormat } from "libraries/date-time-format/date-time-format";
import { PageableTableActions } from "../../../states/pageable-table";
import { Predicates } from "../../../libraries/predicates/predicates";
import useArtworkGenericRequests from "../hooks/useArtworkGenericReaquestsHook";
import { useMasterArtworkMyApprovalContextStore } from "../../../context/MasterArtworkMyApprovalsContext";
import { MyApprovalRequestTypes } from "../../../states/meta-artwork/my-approval-request/data.type";
import { MyApprovalActions } from "../../../states/meta-artwork/my-approval-request";
import { retrieveObjectFromLocalStorage } from "../../../helpers/local-storage.helper";
import {
  ArtworkCollectionPersistentData,
  ArtworkMetaPersistentData,
} from "../../../types/persistent-page-filters";
import {
  mapFiltersToQueryString,
  storeLastFiltersAndResults,
} from "../../../helpers";

const ArtworkApprovalRequestsIdCellRenderer = memo(
  (model: { id: number | string }) => {
    return (
      <div className="d-flex">
        <span className="pe-2 flex-fill align-self-center">{model.id}</span>
        <div></div>
      </div>
    );
  },
);

// TODO: MOVE TO A GENERIC AND SHARE IT ACROSS THE SYSTEM
const CONTEXT_MAPPING = {
  2: { label: "Collection", path: "collections" },
  // 43: { label: "Meta Artwork Name", path: "artworks/name" },
  44: { label: "Meta Artwork Number", path: "meta-artwork" },
  45: { label: "Artwork", path: "printing-technique/" },
};

export const TableArtworkRequest = () => {
  const navigate = useNavigate();

  const {
    state: { dataResults, ...searchParams },
    dispatch,
  } = useMasterArtworkMyApprovalContextStore();

  const { data, isLoading } = useArtworkGenericRequests(searchParams);

  const gridRef = useRef<AgGridReact<Array<any>>>(null);

  const { dispatch: dispatchPageTable } = usePageableTable();

  useEffect(() => {
    dispatchPageTable(PageableTableActions.setLoading(isLoading));
    //@ts-ignore
    if (Predicates.isNotNullAndNotUndefined(dataResults?.results)) {
      //@ts-ignore
      dispatchPageTable(
        PageableTableActions.setTotal(dataResults?.results?.length),
      );
    }
  }, [dataResults, isLoading]);

  useEffect(() => {
    dispatchPageTable(PageableTableActions.setPageSize(searchParams.limit));
    dispatchPageTable(PageableTableActions.setFirstRender(false));
  }, [dispatchPageTable, searchParams.limit]);

  useEffect(() => {
    dispatch(MyApprovalActions.setDataResults(data))
  }, [data]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Artwork Level",
        field: "context.id",
        minWidth: 70,
        maxWidth: 140,
        cellRenderer: (params: any) => (
          <ArtworkApprovalRequestsIdCellRenderer
            // @ts-ignore
            id={CONTEXT_MAPPING[params?.data?.context.id]?.label}
          />
        ),
      },
      {
        headerName: "Code / Name",
        field: "corresponding_object_id",
        minWidth: 70,
        cellRenderer: (params: any) => {
          const pt_code =
            params.data.status === "APPROVED"
              ? `${params.data?.data?.decoration_technique?.id.toString().padStart(2, 0)}-${params.data?.corresponding_object_id.toString().padStart(5, 0)}`
              : null;

          return (
            <>
              {params?.data?.context.id === 45 ? (
                <>
                  {pt_code} - {params.data?.data?.name}
                </>
              ) : (
                <ArtworkApprovalRequestsIdCellRenderer
                  id={
                    !!params?.data?.data?.name
                      ? `${params?.data?.data?.code} - ${params?.data?.data?.name}`
                      : params?.data?.data?.code
                  }
                />
              )}
            </>
          );
        },
      },
      {
        headerName: "Request Date",
        field: "created_at",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => (
          <ArtworkApprovalRequestsIdCellRenderer
            id={DateTimeFormat.formatTimestamp(params?.data?.created_at)}
          />
        ),
      },
      {
        headerName: "Requestor",
        field: "user_name",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          const requestor = params?.data?.data?.requestHistory?.find(
            (history: any) => history?.status?.status === "CREATED",
          );

          return (
            <ArtworkApprovalRequestsIdCellRenderer
              id={!!requestor ? `${requestor?.user?.first_name} ${requestor?.user?.last_name}` : ""}
            />
          );
        },
      },
      {
        headerName: "Requestor Location",
        field: "requestor_location",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          const requestor = params?.data?.data?.requestHistory?.find(
            (history: any) => history.status.status === "CREATED",
          );
          return <>{!!requestor && requestor?.user?.location?.name}</>;
        },
      },
      {
        headerName: "Approved / Rejected Date",
        field: "data.status",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          const lastRequestHistory = params?.data?.data?.requestHistory?.find(
            (history: any) => history?.status?.status === params?.data?.status,
          );

          return (
            <>
              {(params?.data?.status === "APPROVED" ||
                params?.data?.status === "REJECTED") && !!lastRequestHistory?.date ?
                DateTimeFormat.formatTimestamp(lastRequestHistory?.date) :""}
            </>
          );
        },
      },
      {
        headerName: "Approver",
        field: "approver",
        minWidth: 70,
        maxWidth: 140,
        cellRenderer: (params: any) => {
          const lastRequestHistory = params?.data?.data?.requestHistory?.find(
            (history: any) => history?.status?.status === params?.data?.status,
          );

          return (
            <>
              {(params?.data?.status === "APPROVED" ||
                params?.data?.status === "REJECTED") && !!lastRequestHistory?.user?.first_name ?
                `${lastRequestHistory?.user?.first_name} ${lastRequestHistory?.user?.last_name}`:""}
            </>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: 70,
        maxWidth: 100,
        cellRenderer: (params: any) => (
          <ArtworkApprovalRequestsIdCellRenderer id={params?.data?.status} />
        ),
      },
      {
        headerName: "Actions",
        minWidth: 70,
        maxWidth: 100,
        cellRenderer: (params: any) => {
          return (
            <Link
              className="mt-1 me-3"
              to={
                // @ts-ignore
                `${CONTEXT_MAPPING[params?.data?.context.id]?.path}/requests/${params?.data?.id}`
              }
              style={{ textDecoration: "underline" }}
            >
              View
            </Link>
          );
        },
      },
    ],
    [dataResults],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      headerClass: "text-wrap",
      minWidth: 100,
      flex: 1,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
      autoHeight: true,
      suppressMenu: true,
      suppressMovable: true,
      sortable: true,
    }),
    [],
  );

  const tableProp = useMemo(
    () => ({
      gridRef,
      //@ts-ignore
      rowData: dataResults?.results,
      columnDefs,
      defaultColDef,
    }),
    [gridRef, dataResults, columnDefs, defaultColDef],
  );

  return (
    <>
      <PageableTable tableProp={{ ...tableProp }} />
    </>
  );
};
